import { useCallback, useEffect, useRef, useState } from "react"
import { Link, useLocation, useNavigate, useParams } from "react-router-dom"
import { getReading, startOfficerReading, startReading } from "../api"
import type { AppealReading, FreeReading, ReadingLevel, ReadingStage, ReadingView } from "../contract"
import { Beam } from "../ds/Beam"
import { NiraBar } from "../ds/NiraBar"
import { Rail } from "../answer/Rail"
import { lakh, shortDate } from "../answer/format"
import { usePageTitle } from "../answer/hooks"
import "../answer/answer.css"
import "../answer/answer-v4.css"
import "../answer/pro.css"
import "./case.css"
import "./reading.css"
import { Code } from "./Code"
import { AppealPreview } from "./AppealPreview"
import { BeforeTheNotice } from "./BeforeTheNotice"
import { EngineSection, IntentSection, OfficerCards, OfficerStrip, OrderFindings, PaidEdge, ownPeriods, withoutCounts } from "./OfficerReading"
import { paidPreview } from "./paidPreview"
import { CreditNote, LockedRailItem, PreviewDoor } from "./Gated"
import { AddedBanner, ReadingComposer, useHydrate, type Hydrate } from "./Hydrate"
import { track } from "@/lib/analytics"

const inr = new Intl.NumberFormat("en-IN")
const rupees = (n: number) => `₹${inr.format(Math.round(n))}`
const POLL_MS = 1000
/** After this long the reader is told they need not wait; after STALL_S, that it is slow. */
const LEAVE_S = 8
const STALL_S = 45

/** Why the officer's reading did not start, in the server's own words when it gave them. */
export function officerStartError(err: unknown): string {
  const { status, message } = (err ?? {}) as { status?: number; message?: string }
  if (status === 503) return "The officer's reading isn't available right now. Try again later."
  const detail = typeof message === "string" && !/^HTTP \d+$/.test(message) ? message.trim() : ""
  if (status === 409 && detail) return detail
  return detail && status !== undefined && status < 500 ? `${detail} Try again.` : "The officer's reading could not start. Try again."
}

/** A notice reading: the working page while it runs (NA-Working), then the free reading (NA-Preview). */
export default function ReadingPage() {
  const { caseId = "", readingId = "" } = useParams()
  const [view, setView] = useState<ReadingView | null>(null)
  const [failed, setFailed] = useState<"" | "gone" | "error">("")
  const [histOpen, setHistOpen] = useState(false)
  const navigate = useNavigate()
  const [redoing, setRedoing] = useState(false)
  // Bumped to poll again once the officer's reading has been asked for.
  const [pollKey, setPollKey] = useState(0)
  const [asking, setAsking] = useState(false)
  const [officerError, setOfficerError] = useState("")
  // The officer's reading is metered by the gateway (one free, then Pro): a 402 has already opened
  // the paywall, so it needs nothing more here; anything else is said beside the button.
  const runOfficer = async () => {
    setAsking(true)
    setOfficerError("")
    try {
      setView(await startOfficerReading(caseId, readingId))
      setPollKey((k) => k + 1)
    } catch (err) {
      if ((err as { status?: number })?.status !== 402) setOfficerError(officerStartError(err))
    } finally {
      setAsking(false)
    }
  }
  // Appeal or not again, from the day the reader received the order.
  const received = async (iso: string) => {
    setRedoing(true)
    try {
      const next = await startReading(caseId, "order_preview", { communicated_on: iso })
      navigate(`/case/${encodeURIComponent(caseId)}/reading/${encodeURIComponent(next.id)}`)
    } finally {
      setRedoing(false)
    }
  }

  const load = useCallback(async () => {
    try {
      const next = await getReading(caseId, readingId)
      setFailed("")
      // An old link to a case file since folded into another: the reading lives there now.
      if (next.case && next.case.id !== caseId) navigate(`/case/${encodeURIComponent(next.case.id)}/reading/${encodeURIComponent(readingId)}`, { replace: true })
      setView(next)
      return next
    } catch (err) {
      setFailed((err as { status?: number })?.status === 404 ? "gone" : "error")
      return null
    }
  }, [caseId, readingId, navigate])

  useEffect(() => {
    let alive = true
    let timer: ReturnType<typeof setTimeout> | undefined
    const tick = async () => {
      const next = await load()
      const officerRunning = (next?.contract as Partial<FreeReading> | undefined)?.officer?.status === "running"
      if (alive && (next?.status === "running" || officerRunning)) timer = setTimeout(() => void tick(), officerRunning ? POLL_MS * 3 : POLL_MS)
    }
    void tick()
    return () => {
      alive = false
      if (timer) clearTimeout(timer)
    }
  }, [load, pollKey])
  usePageTitle(view?.case?.title)

  const running = view?.status === "running"
  const done = view?.status === "done" && !!view.contract.lead
  // The funnel: each reading counted once when it is first seen finished.
  const seen = useRef("")
  useEffect(() => {
    if (!done || !view || seen.current === view.id) return
    seen.current = view.id
    track("reading_viewed", { kind: view.contract.mode === "order_preview" ? "appeal" : "notice" })
  }, [done, view])
  // Opened at one section (the case file's "Officer's reading" entry): go there once it has drawn.
  const { hash } = useLocation()
  const jumped = useRef(false)
  useEffect(() => {
    if (!done || !hash || jumped.current) return
    jumped.current = true
    document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView?.({ block: "start" })
  }, [done, hash])
  const caseLink = `/case/${encodeURIComponent(caseId)}`
  const hydrate = useHydrate(caseId, view?.kind === "order_preview" ? "order_preview" : "notice_no_reply")
  return (
    <div className="na2 na4 ncf nrd">
      <NiraBar
        onMenu={() => setHistOpen((v) => !v)}
        menuOpen={histOpen}
        menuControls="nira-hlist"
        crumbs={[
          { label: "Workspaces", to: "/workspaces" },
          { label: view?.case?.title ?? "Workspace", to: caseLink },
          { label: view?.kind === "order_preview" ? "Appeal or not" : "The reading" },
        ]}
        beam={running ? "working" : undefined}
      />
      <Rail threadId="" histOpen={histOpen} onHist={setHistOpen} refreshKey={0} drawerOnly caseId={caseId} scope={view?.kind === "order_preview" ? "appeal" : "notice"} />
      <div className={`scrim${histOpen ? " on" : ""}`} aria-hidden="true" onClick={() => setHistOpen(false)} />
      {!view ? (
        <main className="ncf-main">
          {failed === "gone" ? (
            <div className="ncf-msg"><h1>This reading does not exist, or was deleted.</h1><p><Link to={caseLink}>Open the workspace</Link></p></div>
          ) : failed ? (
            <div className="ncf-msg"><h1>The reading could not be opened.</h1><p><button type="button" className="pro-link" onClick={() => void load()}>Try again</button></p></div>
          ) : (
            <p className="ncf-msg" role="status">Opening the reading…</p>
          )}
        </main>
      ) : view.status === "failed" ? (
        <main className="ncf-main">
          <div className="ncf-msg">
            <h1>{view.contract.error ?? (view.kind === "order_preview" ? "Nira could not read the order." : "Nira could not weigh the notice.")}</h1>
            <p>
              {view.kind === "order_preview" ? "The order is kept in the workspace. " : "The notice is kept in the workspace. "}
              <Link to={`${caseLink}/confirm${view.kind === "order_preview" ? "" : "?check=1"}`}>{view.kind === "order_preview" ? "Open it and try again" : "Check them and weigh it again"}</Link>
            </p>
          </div>
        </main>
      ) : running || !view.contract.lead ? (
        <Working view={view} />
      ) : view.contract.mode === "order_preview" ? (
        <AppealPreview
          reading={view.contract as AppealReading}
          caseLink={caseLink}
          connected={view.case?.notice_confirmed ? view.case.title : undefined}
          onReceived={(iso) => void received(iso)}
          busy={redoing}
          level={view.level}
          hydrate={hydrate}
        />
      ) : (
        <Preview reading={view.contract as FreeReading} level={view.level} hydrate={hydrate} caseLink={caseLink} caseTitle={view.case?.title ?? ""} readingId={readingId} onOfficer={() => void runOfficer()} asking={asking} officerError={officerError} />
      )}
    </div>
  )
}

function clock(s: number): string {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`
}

/** NA-Working: the stages land in order, each with what it found. */
export function Working({ view }: { view: ReadingView }) {
  const started = useRef(Date.now())
  const [elapsed, setElapsed] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - started.current) / 1000)), 1000)
    return () => clearInterval(t)
  }, [])
  const stages: ReadingStage[] = view.contract.stages ?? []
  const next = stages.findIndex((s) => !s.done)
  const facts = view.case?.facts
  return (
    <main className="nrd-work" aria-live="polite">
      <div className="nrd-work-head">
        <Beam state="working" size="hero" label={view.kind === "order_preview" ? "Nira is reading the order" : "Nira is weighing the notice"} />
        <h1>{view.kind === "order_preview" ? "Reading the order for an appeal." : "Reading it the way the officer will."}</h1>
        {facts ? (
          <p className="ncf-facts">
            {[facts.state, facts.section ? `s.${facts.section}` : "", facts.periods.length ? `FY ${facts.periods.join(", ")}` : "",
              facts.proposed_total ? `${lakh(facts.proposed_total)}` : ""].filter(Boolean).join(" · ")}
          </p>
        ) : null}
      </div>
      <ol className="nrd-stages">
        {stages.map((s, i) => (
          <li key={s.key} className={s.done ? "done" : i === next ? "now" : "next"}>
            <span className="nrd-tick" aria-hidden="true">{s.done ? "✓" : ""}</span>
            <div>
              <b>{s.label}</b>
              {s.done && s.line ? <span>{s.line}</span> : i === next ? <span>working</span> : <span>next</span>}
            </div>
          </li>
        ))}
      </ol>
      <p className="nrd-time mono">{clock(elapsed)}</p>
      {elapsed >= STALL_S ? (
        <p className="nrd-leave">This is taking longer than usual. It keeps going if you leave; the reading stays in your workspace.</p>
      ) : elapsed >= LEAVE_S ? (
        <p className="nrd-leave">You do not have to wait here. The reading stays in your workspace and opens from there. Messages on WhatsApp when it is ready are coming.</p>
      ) : null}
    </main>
  )
}

const SECTIONS: { n: string; title: string; paid?: boolean }[] = [
  { n: "01", title: "Can this notice stand?" },
  { n: "02", title: "Allegation by allegation" },
  { n: "03", title: "Intent, and what it changes", paid: true },
  { n: "04", title: "What it could cost" },
  { n: "05", title: "What to do, by when" },
  { n: "06", title: "How the order would read", paid: true },
]
/** Past the preview: what each locked section holds, said in the overlay its rail item opens. */
const LOCKED_LINE: Record<string, string> = {
  "03": "Whether the department can prove fraud or suppression, and what falls with it under s.75(2), read the way the officer will.",
  "04": "What this notice could cost: the worst case, what to pay now, interest while it runs, and the likely figure between them.",
  "05": "What to do and by when: the reply date, the days left, and the hearing to ask for.",
  "06": "The order, finding by finding, as the officer would write it, with your answer beside each.",
}
const OFFICER_VIEW = "Nira Pro reads each allegation the way the officer will: the likely call, the strongest point for you, and the order as it would be written, with what it could cost and what to do by when."

const STATUS_CLASS: Record<string, string> = { out: "helps", turns_on_intent: "mixed", in_time: "hurts", unknown: "mixed" }
const FIELD_WORDS: Record<string, string> = {
  section_invoked: "Section", periods: "Periods", notice_date: "Notice date", reply_due: "Reply due",
  hearing: "Hearing", "hearing.offered": "Hearing", tax: "Tax", interest: "Interest", penalty: "Penalty",
}

const CHECK_WORD: Record<string, string> = { stands: "stands", check: "check the notice", ask: "ask for one in the reply" }


/** NA-Preview: what code works out from the notice alone; the officer's likely call is the paid reading. */
export function Preview({ reading: r, level = "full", hydrate, caseLink, readingId = "", onOfficer, asking = false, officerError = "" }: {
  reading: FreeReading; level?: ReadingLevel; hydrate?: Hydrate; caseLink: string; caseTitle?: string; readingId?: string; onOfficer?: () => void; asking?: boolean; officerError?: string
}) {
  // After the free one: the glance, 01 and 02's heads; the server sent nothing past them.
  const gated = level === "preview"
  const [on, setOn] = useState("01")
  // Dev builds only: the reading as a paid reader would see it (?paid=1).
  const [paid] = useState(() => paidPreview())
  // The section the reader is in: the last one whose top has passed the bar.
  useEffect(() => {
    const onScroll = () => {
      let current = SECTIONS[0].n
      for (const s of SECTIONS) {
        const el = document.getElementById(`nrd-${s.n}`)
        if (el && el.getBoundingClientRect().top <= 140) current = s.n
      }
      setOn(current)
    }
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])
  const h = r.header
  const live = r.allegations.filter((a) => a.status !== "counted_with")
  // The officer's reading shows once it has been run on this reading (the gateway admitted it), or
  // in the dev paid preview, where its place is shown before it has run.
  const officer = r.officer ?? null
  const shown = paid || officer !== null
  const read = officer?.status === "done" ? officer : null
  return (
    <div className="nrd-page">
      <nav className="nrd-rail" aria-label="Where you stand">
        <p className="ncf-eyebrow">Where you stand</p>
        {SECTIONS.map((s) => gated && s.n > "02" ? (
          <LockedRailItem key={s.n} n={s.n} title={s.title} line={LOCKED_LINE[s.n]} metric="nira.notice_reading" source="rail_lock" />
        ) : (
          <a key={s.n} href={`#nrd-${s.n}`} className={`${on === s.n ? "on" : ""}${s.paid && !shown ? " paid" : ""}`} aria-current={on === s.n ? "true" : undefined}>
            <span className="mono">{s.n}</span>{s.title}
          </a>
        ))}
        <p className="nrd-rail-note">{gated ? "The time limit and the allegations are worked out here. The rest of this notice is read in Nira Pro." : paid ? "Dev preview: the reading as a paid reader sees it. ?paid=0 to leave." : shown ? "Dates and money are worked out; the calls are the officer's reading, marked as such." : "What is worked out from the notice. The greyed sections have not run yet: they fill in when you start the officer's reading."}</p>
      </nav>

      <main className="nrd-main">
        {hydrate ? <AddedBanner hydrate={hydrate} /> : null}
        <header className="nrd-head">
          <p className="ncf-eyebrow">{gated ? "The computed reading" : "The reading"}</p>
          <h1>{r.lead.line}</h1>
          {r.lead.sub ? <p className="nrd-sub">{r.lead.sub}</p> : null}
          <div className="nrd-chips">
            {h.notice_number ? <span className="nrd-chip mono" title={h.notice_number}>{h.notice_number}</span> : null}
            <span className="nrd-chip">{h.din ? "DIN present" : "No DIN read"}</span>
            <span className="nrd-chip">s.{h.section}</span>
            {h.proposed ? <span className="nrd-chip mono">{rupees(h.proposed)} proposed</span> : null}
            {h.notice_date ? <span className="nrd-chip">notice {shortDate(h.notice_date)}</span> : null}
          </div>
        </header>

        <div className="nrd-glance">
          <a href="#nrd-01"><span className="k">01 Time limit</span><span className="v">{r.glance.time_limit.of ? `${r.glance.time_limit.years_out} of ${r.glance.time_limit.of} ${r.glance.time_limit.of === 1 ? "year" : "years"} out` : "No years read"}</span><span className="s">{r.glance.time_limit.line}</span></a>
          <a href="#nrd-02"><span className="k">02 Allegations</span><span className="v">{r.glance.allegations.live} live{r.glance.allegations.paired ? `, ${r.glance.allegations.paired} paired` : ""}</span><span className="s">{r.glance.allegations.line}</span></a>
          <a href={gated ? "#nrd-locked" : "#nrd-04"}><span className="k">04 Worst case</span><span className="v mono">{lakh(r.glance.worst.amount)}</span><span className="s">{r.glance.worst.line}</span></a>
          <a href={gated ? "#nrd-locked" : "#nrd-05"}><span className="k">05 Reply by</span><span className="v">{r.glance.reply_by.date ? shortDate(r.glance.reply_by.date) : "Not read"}</span><span className="s">{r.glance.reply_by.line}</span></a>
        </div>

        <section className="nrd-sec" id="nrd-01" aria-labelledby="nrd-01-h">
          <h2 id="nrd-01-h">01 · Can this notice stand?</h2>
          <p className="nrd-read">{r.validity.summary}</p>
          <div className="nrd-rows">
            {r.validity.years.map((y) => (
              <Code key={y.fy} line={<><b>FY {y.fy}</b> · {y.line}</>} working={y.working} chip={y.status_word} tone={STATUS_CLASS[y.status]} />
            ))}
            {r.validity.checks.map((c) => (
              <Code key={c.key} line={c.line} working={c.working} chip={CHECK_WORD[c.status]} tone={c.status === "stands" ? "" : "mixed"} />
            ))}
          </div>
        </section>

        <section className="nrd-sec" id="nrd-02" aria-labelledby="nrd-02-h">
          <h2 id="nrd-02-h">02 · Allegation by allegation</h2>
          <p className="nrd-read">
            {live.length} {live.length === 1 ? "allegation" : "allegations"}, ordered by money.{gated ? "" : " What the officer is likely to decide on each is where the heavy work starts."}
          </p>
          <ul className="nrd-algs">
            {r.allegations.map((a) => (
              <li key={a.id} className={a.status === "falls_with_year" || a.status === "counted_with" ? "quiet" : ""}>
                <span className="mono nrd-id">{a.id}</span>
                <span className="nrd-alg-t">
                  <b>{a.title}</b>
                  <span>{[ownPeriods(a, h.years), a.note].filter(Boolean).join(" · ")}</span>
                </span>
                <span className="mono nrd-amt">{a.status === "counted_with" ? `with ${a.paired_with}` : rupees(a.amount)}</span>
              </li>
            ))}
          </ul>
          {gated ? (
            <PreviewDoor door="Read the officer's view of each allegation" title="The officer's view of each allegation" line={OFFICER_VIEW} metric="nira.notice_reading" source="preview_door" />
          ) : shown ? (
            <>
              <OfficerStrip officer={officer} onRun={() => onOfficer?.()} busy={asking} dev={paid} error={officerError} />
              <OfficerCards allegations={live} officer={officer} noticeYears={h.years} hydrate={hydrate} />
            </>
          ) : live[0] ? (
            <>
              <PaidEdge a={live[0]} onRead={() => onOfficer?.()} busy={asking} />
              {officerError ? <p className="nrd-note" role="alert">{officerError}</p> : null}
            </>
          ) : null}
        </section>

        {gated ? null : (<>
        <section className={`nrd-sec${shown ? "" : " paid"}`} id="nrd-03" aria-labelledby="nrd-03-h">
          <h2 id="nrd-03-h">03 · Intent, and what it changes</h2>
          {shown ? (read?.intent ? <IntentSection intent={read.intent} /> : <EngineSection what="Whether intent is made out, and what falls with it under s.75(2)" officer={officer} />) : (
            <p className="nrd-read">Whether the department can prove fraud or suppression, and what falls with it under s.75(2), is in the officer's reading.</p>
          )}
        </section>

        <section className="nrd-sec" id="nrd-04" aria-labelledby="nrd-04-h">
          <h2 id="nrd-04-h">04 · What it could cost</h2>
          <div className="nrd-rows">
            <Code line={r.money.worst.line} working={r.money.worst.working} chip={lakh(r.money.worst.amount)} />
            <Code line={r.money.pay_now.line} working={r.money.pay_now.working} chip={lakh(r.money.pay_now.amount)} />
            <Code line="Interest while it runs, s.50 at 18%" working={r.money.interest_per_month.working} chip={`+${rupees(r.money.interest_per_month.amount)} a month`} />
            {read?.standing ? (
              <Code
                line={withoutCounts(read.standing.line)}
                working={withoutCounts(read.standing.working)}
                chip={read.standing.low === read.standing.high ? lakh(read.standing.low) : `${lakh(read.standing.low)} to ${lakh(read.standing.high)}`}
                tone={read.standing.low === read.standing.high ? "" : "mixed"}
              />
            ) : null}
          </div>
          <p className="nrd-note">Interest to today: {r.money.interest.working}.{read?.standing ? "" : " The likely figure, between these, is in the officer's reading."}</p>
        </section>

        <section className="nrd-sec" id="nrd-05" aria-labelledby="nrd-05-h">
          <h2 id="nrd-05-h">05 · What to do, by when</h2>
          <div className="nrd-todo">
            <div><span className="k">Reply by</span><span className="v">{r.actions.reply_by ? shortDate(r.actions.reply_by) : "Not read"}</span></div>
            <div>
              <span className="k">Days left</span>
              <span className="v mono">{r.actions.days_left === null ? "—" : r.actions.days_left >= 0 ? r.actions.days_left : "Passed"}</span>
              {r.actions.days_left !== null && r.actions.days_left < 0 ? <span className="s">{r.actions.line}</span> : null}
            </div>
          </div>
          {r.actions.hearing_to_ask ? <p className="nrd-read">The notice offers no personal hearing. Ask for one under s.75(4) in the first paragraph of the reply.</p> : null}
          <p className="nrd-note">Kept in your workspace with the notice. <Link to={caseLink}>Open the workspace</Link></p>
        </section>

        {r.exposure_hint ? <BeforeTheNotice hint={r.exposure_hint} /> : null}

        <section className={`nrd-sec${shown ? "" : " paid"}`} id="nrd-06" aria-labelledby="nrd-06-h">
          <h2 id="nrd-06-h">06 · How the order would read</h2>
          {shown ? (read ? <OrderFindings officer={read} allegations={r.allegations} /> : <EngineSection what="The order, finding by finding, with your answer beside each" officer={officer} />) : (
            <p className="nrd-read">The order, finding by finding, as the officer would write it, with your answer beside each. In the officer's reading.</p>
          )}
        </section>
        </>)}

        {r.limits.length ? (
          <section className="nrd-limits" aria-label="What this reading assumes">
            <p className="ncf-eyebrow">What this reading assumes</p>
            <ul>{r.limits.map((l) => <li key={l}>{l}</li>)}</ul>
            <p className="nrd-note">Computed on {shortDate(r.computed_on)} from the facts on the notice. Research, not legal advice.</p>
          </section>
        ) : null}
        <section className="nrd-limits" aria-label="The facts this reading rests on">
          <p className="ncf-eyebrow">The facts this reading rests on</p>
          {r.unsure?.length ? (
            <ul>{r.unsure.map((u) => <li key={u.field}><b>{FIELD_WORDS[u.field.replace(/^A\d+\./, "")] ?? u.field}:</b> {u.why}</li>)}</ul>
          ) : null}
          <p className="nrd-note">
            {r.unsure?.length ? "Nira read these with doubt and went on; nothing waited on you. " : "Read from the notice as printed. "}
            <Link to={`${caseLink}/confirm?check=1${readingId ? `&from=${encodeURIComponent(readingId)}` : ""}`}>Check the facts Nira read</Link>
          </p>
        </section>
        {level === "free" ? <CreditNote of="notice" /> : null}
        {hydrate ? <ReadingComposer hydrate={hydrate} draftFor={live[0] ? { id: live[0].id, title: live[0].title } : undefined} /> : null}
      </main>
    </div>
  )
}
